"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AraasRoom = void 0;
const analytics_1 = require("@streem/analytics");
const araas_client_sdk_1 = require("@streem/araas-client-sdk");
const logger_1 = require("@streem/logger");
const sdk_lightshow_1 = require("@streem/sdk-lightshow");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const logger_2 = require("../util/logger");
const media_1 = require("../util/media");
const audio_tracks_1 = require("./tracks/audio_tracks");
const data_tracks_1 = require("./tracks/data_tracks");
const video_tracks_1 = require("./tracks/video_tracks");
class AraasRoom {
    constructor(config) {
        this.config = config;
        this.connectionStatusSubject = new rxjs_1.BehaviorSubject('DISCONNECTED');
        this.errorCodeSubject = new rxjs_1.BehaviorSubject(undefined);
        this.recordingSubject = new rxjs_1.BehaviorSubject(false);
        // region local subjects
        this.localConnectionSubject = new rxjs_1.BehaviorSubject(false);
        this.localNetworkSubject = new rxjs_1.BehaviorSubject(0);
        this.localAudioSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localVideoStream = new rxjs_1.BehaviorSubject(undefined);
        this.localAudioErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localVideoSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localVideoElement = new rxjs_1.BehaviorSubject(undefined);
        this.localVideoErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localDataSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localDataErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        // endregion
        // region remote subjects
        this.remoteConnectionSubject = new rxjs_1.BehaviorSubject(false);
        this.remoteNetworkSubject = new rxjs_1.BehaviorSubject(0);
        this.remoteAudioSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteAudioErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteVideoSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteVideoElement = new rxjs_1.BehaviorSubject(undefined);
        this.remoteVideoErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteDataSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteDataErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteDataTrackEmitter = new rxjs_1.BehaviorSubject(undefined);
        // endregion
        this.mediaWebRtcStatsSubject = new rxjs_1.Subject();
        this.mediaWebRtcStats = this.mediaWebRtcStatsSubject.asObservable();
        this.connectionState = this.connectionStatusSubject.asObservable();
        this.errorCode = this.errorCodeSubject.pipe((0, operators_1.skip)(1));
        this.recording = this.recordingSubject.asObservable();
        // region local observables
        this.localNetworkQuality = this.localNetworkSubject.asObservable();
        this.localConnection = this.localConnectionSubject.asObservable();
        this.localAudioTrack = this.localAudioSubject.asObservable();
        this.localAudioErrorCode = this.localAudioErrorSubject.asObservable();
        this.localVideoTrack = this.localVideoSubject.asObservable();
        this.localVideoErrorCode = this.localVideoErrorSubject.asObservable();
        this.localDataTrack = this.localDataSubject.asObservable();
        this.localDataErrorCode = this.localDataErrorSubject.asObservable();
        // endregion
        // region remote observables
        this.remoteNetworkQuality = this.remoteNetworkSubject.asObservable();
        this.remoteConnection = this.remoteConnectionSubject.asObservable();
        this.remoteAudioTrack = this.remoteAudioSubject.asObservable();
        this.remoteAudioErrorCode = this.remoteAudioErrorSubject.asObservable();
        this.remoteVideoTrack = this.remoteVideoSubject.asObservable();
        this.remoteVideoErrorCode = this.remoteVideoErrorSubject.asObservable();
        this.remoteDataTrack = this.remoteDataSubject.asObservable();
        this.remoteDataErrorCode = this.remoteDataErrorSubject.asObservable();
        // endregion
        // user media request error observables
        this.getUserMediaVideoErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.getUserMediaVideoError = this.getUserMediaVideoErrorSubject.asObservable();
        this.getUserMediaAudioErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.getUserMediaAudioError = this.getUserMediaAudioErrorSubject.asObservable();
        this.trackProviderName = 'ARaaS';
        this.useNamedVideoTracks = false;
        this.stopAndRemoveTrack = (mediaStream) => {
            return (track) => {
                track.stop();
                mediaStream.removeTrack(track);
            };
        };
        this.lightshowEngine = new sdk_lightshow_1.LightshowEngine(this.config.remoteParticipantCapabilities);
    }
    connect(roomId, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            logger_2.logger.info(`User ${userId.toString()} joining room ${roomId.toString()}`);
            this.roomId = roomId;
            this.userId = userId;
            this.araasSession = new araas_client_sdk_1.ARaaSSession(logger_2.logger.extend('ARaaS'), (message) => {
                const isLocalUser = message.name === userId.toString();
                switch (message.id) {
                    case 'createVideoElement':
                        if (!isLocalUser) {
                            const videoEl = (0, media_1.createVideoElement)();
                            this.remoteVideoElement.next(videoEl);
                        }
                        break;
                    case 'passDownElements':
                        logger_2.logger.debug(`[ARAAS messageHandler] passing element to araas for ${message.name}`);
                        this.araasSession.passDownElements({
                            name: message.name,
                            videoElement: isLocalUser
                                ? this.localVideoElement.value
                                : this.remoteVideoElement.value,
                        });
                        break;
                    case 'iceCandidate':
                        logger_2.logger.debug('[ARAAS messageHandler] iceCandidate: ', message);
                        break;
                    case 'dataChannelOpened':
                        logger_2.logger.debug('[ARAAS messageHandler] dataChannelOpened');
                        break;
                    case 'dataChannelClosed':
                        logger_2.logger.debug('[ARAAS messageHandler] dataChannelClosed');
                        break;
                }
            });
            this.araasSession.joinRoom({
                videoStream: this.localVideoStream.value,
                dataChannels: true,
                wsUri: this.config.roomVideoCredentials.webSocketUri,
                userName: userId.toString(),
                roomName: roomId.toString(),
                mediaConstraints: this.mediaStreamConstraints,
                onTrack: (event) => {
                    if (event.track.kind === 'audio') {
                        this.remoteAudioSubject.next(new audio_tracks_1.AraasRemoteAudioTrack(event.track));
                    }
                    if (event.track.kind === 'video') {
                        this.remoteVideoSubject.next(new video_tracks_1.AraasRemoteVideoTrack(this.remoteVideoElement.value, this.lightshowEngine, event.track));
                    }
                },
                dataChannelConfig: {
                    onmessage: (event) => {
                        if (this.remoteDataTrackEmitter.value) {
                            this.remoteDataTrackEmitter.value.emitMessage(event.data);
                        }
                    },
                    onopen: () => {
                        logger_2.logger.debug('[ARaaS Room] data channel onopen');
                        this.remoteDataTrackEmitter.next(new data_tracks_1.RemoteDataTrack());
                        const araasDataTrack = new data_tracks_1.AraasRemoteDataTrack(this.remoteDataTrackEmitter.value);
                        this.remoteDataSubject.next(araasDataTrack);
                        this.lightshowEngine.connect(araasDataTrack.messages);
                    },
                    onclose: () => logger_2.logger.debug('[ARaaS Room] data channel onclose'),
                    onerror: () => logger_2.logger.debug('[ARaaS Room] data channel onerror'),
                    onbufferedamountlow: () => console.debug('[ARaaS Room] onbufferamountlow'),
                },
            });
            // Logging
            (0, media_1.getPermissionsState)()
                .then(permissionsState => {
                logger_2.logger.setContextValue(logger_1.contextValues.PERMISSIONS_STATE, permissionsState);
            })
                .catch(() => { });
            // set up 15 second interval to get and log stats
            this.getStatsIntervalId = window.setInterval(() => this.getStats(), 15 * 1000);
        });
    }
    /**
     * createMediaStream will create the MediaStream needed to connect with the track provider
     *
     * ARaaS uses *one* MediaStream for *all* MediaStreamTracks.
     * ARaaS accomplishes this by using an HTMLVideoElement as the container for this MediaStream (even when there is no video!)
     *
     * Twilio uses *one* MediaStream for *every* MediaStreamTrack
     *
     * @param config configuration of media constraints, including which media to create
     * @returns `MediaStreamResult` which includes the tracks created and optionally their names
     */
    createMediaStream(config) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.mediaStreamConstraints = config.constraints;
            // Check for Microphone and Camera Inputs
            try {
                const hasMic = yield (0, media_1.hasMicrophone)();
                const hasCam = yield (0, media_1.hasCamera)();
                if (!hasMic) {
                    logger_2.logger.warn('User does not have a microphone available. Continuing without creating local audio track.');
                }
                if (!hasCam) {
                    logger_2.logger.warn('User does not have a camera available. Continuing without creating local video track.');
                }
            }
            catch (error) {
                logger_2.logger.error('Failed to detect microphone and/or camera input: ', error);
            }
            const mediaStream = yield this.getMediaStream(config.constraints);
            this.localVideoStream.next(mediaStream);
            const videoEl = (0, media_1.createVideoElement)();
            this.localVideoElement.next(videoEl);
            // Create Video Track
            const videoStreamTrack = mediaStream.getVideoTracks()[0];
            if (((_a = config.constraints) === null || _a === void 0 ? void 0 : _a.video) && videoStreamTrack) {
                this.localVideoSubject.next(new video_tracks_1.AraasLocalVideoTrack(videoEl, this.lightshowEngine, videoStreamTrack, mediaStream));
            }
            // Create Audio Track
            const audioStreamTrack = mediaStream.getAudioTracks()[0];
            if (((_b = config.constraints) === null || _b === void 0 ? void 0 : _b.audio) && audioStreamTrack) {
                this.localAudioSubject.next(new audio_tracks_1.AraasLocalAudioTrack(audioStreamTrack));
            }
            // Create Data Track
            this.localDataSubject.next(new data_tracks_1.AraasLocalDataTrack({
                sendMessage: (buffer) => this.araasSession.dataChannelSend(buffer),
            }));
            return {
                audio: !!this.localAudioSubject.value,
                video: !!this.localVideoSubject.value,
                data: !!this.localDataSubject.value,
            };
        });
    }
    flipLocalCamera(_) {
        return __awaiter(this, void 0, void 0, function* () {
            // TODO: implement araas camera flipping
            return Promise.resolve(true);
        });
    }
    getStats() {
        return __awaiter(this, void 0, void 0, function* () {
            // stat types
            const INBOUND_RTP = 'inbound-rtp';
            const REMOTE_INBOUND_RTP = 'remote-inbound-rtp';
            const OUTBOUND_RTP = 'outbound-rtp';
            const MEDIA_SOURCE = 'media-source';
            const TRACK = 'track';
            // stat properties
            const PACKETS_SENT = 'packetsSent';
            const PACKETS_RECEIVED = 'packetsReceived';
            const PACKETS_LOST = 'packetsLost';
            const BYTES_SENT = 'bytesSent';
            const BYTES_RECEIVED = 'bytesReceived';
            const JITTER = 'jitter';
            const ROUND_TRIP_TIME = 'roundTripTime';
            const AUDIO_LEVEL = 'audioLevel';
            const FRAME_HEIGHT = 'frameHeight';
            const FRAME_WIDTH = 'frameWidth';
            const WIDTH = 'width';
            const HEIGHT = 'height';
            const TRACK_IDENTIFIER = 'trackIdentifier';
            const FRAMES_PER_SECOND = 'framesPerSecond';
            const CODEC_ID = 'codecId';
            const VIDEO = 'video';
            const AUDIO = 'audio';
            const MAX_AUDIO_LEVEL = 32767.0;
            const localAudioStats = {};
            const localVideoStats = {};
            const remoteAudioStats = {};
            const remoteVideoStats = {};
            const participantStatsList = yield this.araasSession.getStats();
            const convertSecondsToMS = (seconds) => Math.round(seconds * 1000);
            for (const participantStats of participantStatsList) {
                const isLocalUser = participantStats.name === this.userId.toString();
                const isRemoteUser = !isLocalUser;
                for (const stats of participantStats.values()) {
                    switch (stats['type']) {
                        case INBOUND_RTP:
                            if (stats.kind === VIDEO) {
                                if (isLocalUser) {
                                    localVideoStats.packetsLost = stats[PACKETS_LOST];
                                }
                                else if (isRemoteUser) {
                                    remoteVideoStats.packetsLost = stats[PACKETS_LOST];
                                    remoteVideoStats.packetsReceived = stats[PACKETS_RECEIVED];
                                    remoteVideoStats.bytesReceived = stats[BYTES_RECEIVED];
                                }
                            }
                            else if (stats.kind === AUDIO) {
                                if (isLocalUser) {
                                    localAudioStats.packetsLost = stats[PACKETS_LOST];
                                    localAudioStats.jitterMs = stats[JITTER];
                                }
                                else if (isRemoteUser) {
                                    remoteAudioStats.packetsLost = stats[PACKETS_LOST];
                                    remoteAudioStats.packetsReceived = stats[PACKETS_RECEIVED];
                                    remoteAudioStats.bytesReceived = stats[BYTES_RECEIVED];
                                }
                            }
                            break;
                        case REMOTE_INBOUND_RTP:
                            if (isLocalUser) {
                                if (stats.kind === AUDIO) {
                                    localAudioStats.roundTripTimeMs = convertSecondsToMS(stats[ROUND_TRIP_TIME]);
                                }
                                else if (stats.kind === VIDEO) {
                                    localVideoStats.roundTripTimeMs = convertSecondsToMS(stats[ROUND_TRIP_TIME]);
                                }
                            }
                            break;
                        case OUTBOUND_RTP:
                            if (isLocalUser) {
                                if (stats.kind === AUDIO) {
                                    localAudioStats.codec = stats[CODEC_ID];
                                    localAudioStats.packetsSent = stats[PACKETS_SENT];
                                    localAudioStats.bytesSent = stats[BYTES_SENT];
                                }
                                else if (stats.kind === VIDEO) {
                                    localVideoStats.bytesSent = stats[BYTES_SENT];
                                    localVideoStats.packetsSent = stats[PACKETS_SENT];
                                    localVideoStats.codec = stats[CODEC_ID];
                                    localVideoStats.captureDimensions = {
                                        height: stats[FRAME_HEIGHT],
                                        width: stats[FRAME_WIDTH],
                                    };
                                }
                            }
                            break;
                        case MEDIA_SOURCE:
                            if (stats.kind === AUDIO) {
                                localAudioStats.audioLevel = stats[AUDIO_LEVEL] * MAX_AUDIO_LEVEL;
                                localAudioStats.trackSid = stats[TRACK_IDENTIFIER];
                            }
                            else if (stats.kind === VIDEO) {
                                localVideoStats.frameRate = stats[FRAMES_PER_SECOND];
                                localVideoStats.trackSid = stats[TRACK_IDENTIFIER];
                                localVideoStats.dimensions = {
                                    height: stats[HEIGHT],
                                    width: stats[WIDTH],
                                };
                            }
                            break;
                        case TRACK:
                            if (stats.kind === AUDIO) {
                                remoteAudioStats.trackSid = stats[TRACK_IDENTIFIER];
                            }
                            else if (stats.kind === VIDEO) {
                                remoteVideoStats.trackSid = stats[TRACK_IDENTIFIER];
                            }
                            break;
                    }
                }
            }
            (0, analytics_1.recordWebRTCStats)(this.roomId.toString(), {
                localAudio: [localAudioStats],
                remoteAudio: [remoteAudioStats],
                localVideo: [localVideoStats],
                remoteVideo: [remoteVideoStats],
            });
        });
    }
    disconnect() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            (_a = this.araasSession) === null || _a === void 0 ? void 0 : _a.leaveRoom();
            this.lightshowEngine.disconnect();
            window.clearInterval(this.getStatsIntervalId);
            this.resetState();
        });
    }
    /**
     * This will obtain the user's video and audio one step at a time by breaking the constraints
     * into an audio and video request.
     *
     * Mobile Safari has issues obtaining video well when audio and video is requested together.
     * This is why we break this up into two steps.
     *
     * This bug might be related to the following webkit issues:
     * https://bugs.webkit.org/show_bug.cgi?id=207888
     * @param constraint audio & video constraints
     * @returns MediaStream
     */
    getMediaStream(constraint) {
        return __awaiter(this, void 0, void 0, function* () {
            const tracks = [];
            let videoMediaStreamTrack;
            if (constraint === null || constraint === void 0 ? void 0 : constraint.video) {
                try {
                    const media = yield navigator.mediaDevices.getUserMedia({
                        video: constraint.video,
                    });
                    videoMediaStreamTrack = media.getVideoTracks()[0];
                }
                catch (error) {
                    // TODO indicate to user that track creation was not successful
                    // https://streempro.atlassian.net/browse/STREEM-8679
                    logger_2.logger.error('Failed to get video MediaStreamTrack for ARaaS: ', error);
                }
            }
            if (videoMediaStreamTrack) {
                tracks.push(videoMediaStreamTrack);
            }
            let audioMediaStreamTrack;
            if (constraint === null || constraint === void 0 ? void 0 : constraint.audio) {
                try {
                    const media = yield navigator.mediaDevices.getUserMedia({
                        audio: constraint.audio,
                    });
                    audioMediaStreamTrack = media.getAudioTracks()[0];
                }
                catch (error) {
                    // TODO indicate to user that track creation was not successful
                    // https://streempro.atlassian.net/browse/STREEM-8679
                    logger_2.logger.error('Failed to get audio MediaStreamTrack for ARaaS: ', error);
                }
            }
            if (audioMediaStreamTrack) {
                tracks.push(audioMediaStreamTrack);
            }
            const mediaStream = new MediaStream();
            tracks.forEach((t) => mediaStream.addTrack(t));
            return mediaStream;
        });
    }
    /**
     * Resets all subjects in TwilioRoom
     */
    resetState() {
        // Stop all streams in progress
        // TODO ARaaS package should handle this, but it doesn't work there for some reason.
        // Alex
        this.stopMediaStream(this.localVideoElement.value.srcObject);
        this.localVideoElement.value.srcObject = null;
        this.araasSession = null;
        this.mediaStreamConstraints = null;
        this.connectionStatusSubject.next('DISCONNECTED');
        this.errorCodeSubject.next(undefined);
        this.recordingSubject.next(false);
        /**
         * Local Video
         */
        this.localVideoStream.next(undefined);
        this.localVideoElement.next(undefined);
        this.localVideoErrorSubject.next(undefined);
        this.localVideoSubject.next(undefined);
        /**
         * Local Audio
         */
        this.localAudioErrorSubject.next(undefined);
        this.localAudioSubject.next(undefined);
        /**
         * Local Data
         */
        this.localDataErrorSubject.next(undefined);
        this.localDataSubject.next(undefined);
        /**
         * Local Network
         */
        this.localConnectionSubject.next(false);
        this.localNetworkSubject.next(0);
        /**
         * Remote Video
         */
        this.remoteVideoElement.next(undefined);
        this.remoteVideoSubject.next(undefined);
        this.remoteVideoErrorSubject.next(undefined);
        /**
         * Remote Audio
         */
        this.remoteAudioErrorSubject.next(undefined);
        this.remoteAudioSubject.next(undefined);
        /**
         * Remote Data
         */
        this.remoteDataTrackEmitter.next(undefined);
        this.remoteDataErrorSubject.next(undefined);
        this.remoteDataSubject.next(undefined);
        /**
         * Remote Network
         */
        this.remoteConnectionSubject.next(false);
        this.remoteNetworkSubject.next(0);
    }
    stopMediaStream(mediaStream) {
        if (!mediaStream) {
            return;
        }
        mediaStream.getTracks().forEach(this.stopAndRemoveTrack(mediaStream));
    }
}
exports.AraasRoom = AraasRoom;
//# sourceMappingURL=araas_room.js.map